import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box } from '@mui/material'
import { Button } from '../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../constants/colors'
import { SignedInState, SignedInSteps, SignedInViewProps, DEFAULT_SIGNED_IN_STATE } from '../types/SignedInTypes'
import useAppState from '../hooks/useAppState'
import { onPressContinue, getFirstStep } from '../libs/SignedInMachine'
import { onLoginToIntraction } from '../libs/onLoginToIntraction'
import SignedInOnboarding from './SignedInOnboarding'
import SignedInSignUpForm from './SignedInSignUpForm'
import SignedInGoToDashboard from './SignedInGoToDashboard'

const getViews = (): { [property in SignedInSteps]: React.FC<SignedInViewProps> } => ({
  [SignedInSteps.GO_TO_DASHBOARD]: SignedInGoToDashboard,
  [SignedInSteps.SIGN_UP_FORM]: SignedInSignUpForm,
  [SignedInSteps.ONBOARDING]: SignedInOnboarding
})

type SignedInProps = {
  step?: SignedInSteps
  defaultStep?: SignedInSteps
  enableCreateNewTeam?: boolean
  onClickLater?: () => unknown
}

export default function SignedIn(props: SignedInProps = {}) {
  const { step, defaultStep, enableCreateNewTeam = true, onClickLater } = props
  const { state, setState } = useAppState()
  const navigate = useNavigate()
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingStep, setIsCheckingStep] = useState(true)
  const [signedInState, setSignedInState] = useState<SignedInState>(DEFAULT_SIGNED_IN_STATE)

  // Initialize the first step
  useEffect(() => {
    const initializeStep = async () => {
      setIsCheckingStep(true)

      // Wait for the auth listener to settle before deciding anything.
      if (!state.isAuthInitialized) return

      // Authenticated session but no user doc loaded (e.g. a multi-tab account switch
      // where hydration didn't populate this tab) — go to /login to reload, instead
      // of looping on this page.
      if (!state.user?.id) {
        setIsCheckingStep(false)
        navigate('/login')
        return
      }

      if (step) {
        setSignedInState({ ...signedInState, step })
        setIsCheckingStep(false)
        return
      }

      if (defaultStep) {
        setSignedInState({ ...signedInState, step: defaultStep })
        setIsCheckingStep(false)
        return
      }

      // Get the first step based on user state
      try {
        const firstStep = await getFirstStep(state, {})

        if (firstStep) {
          setSignedInState({ ...signedInState, step: firstStep })
          setIsCheckingStep(false)
        } else {
          // No step needed, go directly to dashboard based on user role
          await onLoginToIntraction(state, { navigate, window, locationState: location.state })
        }
      } catch (error) {
        // On error, go directly to dashboard based on user role
        await onLoginToIntraction(state, { navigate, window, locationState: location.state })
      }
    }

    initializeStep()
  }, [step, defaultStep, state])

  const handlePressContinue = async (newSignedInState: SignedInState) => {
    setIsLoading(true)
    try {
      const updatedState = await onPressContinue(newSignedInState, state, setState)
      setSignedInState(updatedState)

      // If step is undefined, navigate to appropriate dashboard based on user role
      // Note: onPressContinue updates the state via setState, so state should be fresh
      if (!updatedState.step) {
        await onLoginToIntraction(state, { navigate, window, locationState: location.state })
      }
    } catch (error) {
    } finally {
      setIsLoading(false)
    }
  }

  const handleClickBack = () => {
    // TODO: Implement back navigation logic
  }

  const handleClickLater = () => {
    if (onClickLater) {
      onClickLater()
    } else {
      navigate('/')
    }
  }

  const signedInViewProps: SignedInViewProps = {
    isLoading,
    signedInState,
    enableCreateNewTeam,
    onPressContinue: handlePressContinue,
    onClickBack: handleClickBack,
    onClickLater: handleClickLater,
    onClickCrateTeam: () => {} // No-op since create team is removed
  }

  const CurrentView = getViews()[signedInState.step]

  // Show loading state while checking which step to show
  if (isCheckingStep) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: '100vh',
          backgroundColor: INTERAX_COLORS.offBlack,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <p className="text-thunder-grey">Loading...</p>
        </Box>
      </Box>
    )
  }

  if (!CurrentView) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: '100vh',
          backgroundColor: INTERAX_COLORS.offBlack,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <h1 className="text-2xl font-semibold text-white mb-4">Unknown Step</h1>
          <p className="text-thunder-grey mb-4">Step: {signedInState.step}</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            Go Home
          </Button>
        </Box>
      </Box>
    )
  }

  return <CurrentView {...signedInViewProps} />
}
